import React from 'react';

import type { CapabilityLockedBannerProps, TierMin } from './types';

const TIER_LABELS: Record<TierMin, string> = {
  solo: 'Solo',
  team: 'Team',
  professional: 'Professional',
  regulated: 'Regulated',
};

/**
 * CapabilityLockedBanner — defense-in-depth visual.
 *
 * Pattern DS v2.2.0 — primitivo universal D-B (defense-in-depth).
 * gate-by-component: quando capability ausente, componente mostra
 * placeholder neutro em vez de omitir silenciosamente.
 *
 * Usado em A1/A5/B1/B3/B4/B5/B6 + Brief 4 todas as 14 telas variants tier.
 * Origem: Pattern Class 6 Brief 5 R1 cristalizado + Brief 5 A5 auto-auditoria.
 *
 * Acessibilidade:
 * - role="status" para leitores de tela
 * - aria-label descrevendo a restrição
 */
export function CapabilityLockedBanner({
  capability,
  tierMin,
  fallbackMessage,
  variant = 'inline',
  upgradeUrl,
  upgradeLabel = 'Atualizar plano',
}: CapabilityLockedBannerProps) {
  // Copy funcional sem cap-code (jargão dev fica em data-capability, inspecionável).
  const message =
    fallbackMessage ??
    (tierMin
      ? `Esta funcionalidade requer o plano ${TIER_LABELS[tierMin]} ou superior.`
      : 'Seu perfil não tem acesso a esta funcionalidade.');

  if (variant === 'inline') {
    return (
      <div
        role="status"
        aria-label="Funcionalidade bloqueada por permissão"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-2, 8px)',
          padding: 'var(--space-2, 8px) var(--space-3, 12px)',
          background: 'var(--bg-subtle, #f9fafb)',
          border: '1px solid var(--border-default, #e5e7eb)',
          borderRadius: 'var(--radius-md, 7px)',
          fontSize: 'var(--text-xs, 11px)',
          color: 'var(--fg-3, #6b7280)',
          fontFamily: 'var(--font-sans, system-ui)',
        }}
        data-testid="capability-locked-banner"
        data-capability={capability}
        data-tier-min={tierMin}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 14,
            height: 14,
            flexShrink: 0,
            fontSize: 11,
          }}
          aria-hidden="true"
        >
          🔒
        </span>
        <span>{message}</span>
        {upgradeUrl && (
          <a
            href={upgradeUrl}
            style={{
              color: 'var(--brand, #d97706)',
              fontWeight: 'var(--fw-medium, 500)',
              textDecoration: 'none',
              marginLeft: 'var(--space-1, 4px)',
              flexShrink: 0,
            }}
            data-testid="capability-locked-upgrade-link"
          >
            {upgradeLabel}
          </a>
        )}
      </div>
    );
  }

  // Block variant
  return (
    <div
      role="status"
      aria-label="Funcionalidade bloqueada por permissão"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3, 12px)',
        padding: 'var(--space-4, 16px)',
        background: 'var(--bg-subtle, #f9fafb)',
        border: '1px solid var(--border-default, #e5e7eb)',
        borderRadius: 'var(--radius-lg, 9px)',
        width: '100%',
        fontFamily: 'var(--font-sans, system-ui)',
      }}
      data-testid="capability-locked-banner"
      data-capability={capability}
      data-tier-min={tierMin}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md, 7px)',
          background: 'var(--bg-surface, #fff)',
          border: '1px solid var(--border-default, #e5e7eb)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        🔒
      </div>
      <div style={{ flex: 1 }}>
        <p
          style={{
            fontSize: 'var(--text-sm, 12px)',
            fontWeight: 'var(--fw-medium, 500)',
            color: 'var(--fg-1, #111)',
            margin: 0,
          }}
        >
          Funcionalidade indisponível
        </p>
        <p
          style={{
            fontSize: 'var(--text-xs, 11px)',
            color: 'var(--fg-3, #6b7280)',
            margin: '2px 0 0',
          }}
        >
          {message}
        </p>
      </div>
      {upgradeUrl && (
        <a
          href={upgradeUrl}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 28,
            padding: '0 var(--space-3, 12px)',
            background: 'var(--brand, #d97706)',
            color: 'var(--fg-on-accent, #fff)',
            borderRadius: 'var(--radius-md, 7px)',
            fontSize: 'var(--text-xs, 11px)',
            fontWeight: 'var(--fw-semibold, 600)',
            textDecoration: 'none',
            flexShrink: 0,
          }}
          data-testid="capability-locked-upgrade-link"
        >
          {upgradeLabel}
        </a>
      )}
    </div>
  );
}
